// workingState.js — pure "is the agent thinking?" predicate for the chat composer.
// working = the user has sent a turn whose run has STARTED and not yet reached a terminal event.
// (BUG-3 fix: the old ":49" first-token clause was removed — a streamed token no longer clears
//  "working"; only a RUN_FINISHED/RUN_ERROR with seq > sentAtSeq does. The indicator now stays up
//  across the whole turn, alongside the streaming bubble, until the run terminates.)
// Driven ONLY by the durable stream + a local "a turn is in flight" flag — NEVER by the
// `sending` HTTP boolean (which clears at HTTP 201, before the agent emits RUN_STARTED).
//
// events:            durableEvents(streamState) — the seq-sorted durable envelope list
// liveDeltas:        streamState.liveDeltas — { [messageId]: string } ephemeral typewriter
// sentSinceTerminal: TRUE once the user sends; we set it on local send (so the indicator
//                    shows in the RUN_STARTED-after-delta flicker window) and the predicate
//                    flips it off when a token/terminal for THIS send's run arrives.
// sentAtSeq:         CHAT-8 — the max durable event seq at the instant the user sent. The "done"
//                    checks gate on seq > sentAtSeq so a STALE prior run's terminal/token (which
//                    necessarily has seq <= sentAtSeq) can NEVER clear the spinner for a fresh
//                    send. Without it, on every 2nd+ message the window between the local send and
//                    run2's server RUN_STARTED would compare against run1 (the latest *started*
//                    run) whose terminal already landed → spinner wrongly drops.

// Item A (X1) — the interaction-resolution rule is THE shared module, never re-derived here:
// a TOOL_CALL_RESULT with a TOP-LEVEL payload.interaction_id resolves; the propose tool's own
// result (id only inside a stringified content field) does NOT. See interactionResolution.js.
import { resolvedInteractionIdSet } from "../shell/interactionResolution.js"

export function latestRunStartedSeq(events) {
  let seq = 0
  for (const e of events) if (e.type === "RUN_STARTED" && e.seq > seq) seq = e.seq
  return seq            // 0 == no run started yet
}

// A token/delta is considered "for this send" only when it is STRICTLY newer than the send's
// baseline seq. liveDeltas are ephemeral (no seq) — any buffered delta is by definition current.
export function firstTokenArrived(events, liveDeltas, sinceSeq) {
  // any ephemeral delta currently buffered → a token is streaming for the live run
  if (Object.keys(liveDeltas || {}).length > 0) return true
  // any durable assistant content STRICTLY AFTER the send baseline → token arrived for this send
  return (events || []).some(
    (e) => e.type === "TEXT_MESSAGE_CONTENT" && e.seq > sinceSeq
  )
}

export function runTerminated(events, sinceSeq) {
  return (events || []).some(
    (e) => (e.type === "RUN_FINISHED" || e.type === "RUN_ERROR") && e.seq > sinceSeq
  )
}

// THE predicate. working === a turn is in flight AND no terminal for THIS send's run yet.
// Baseline for "this send" is sentAtSeq (the max seq when the user sent). Fallback: when sentAtSeq
// is not provided (legacy callers), derive the baseline as one-less-than the latest RUN_STARTED so
// the original ">= runStartedSeq" semantics are preserved (terminal AT the started run count).
export function isWorking({ events = [], liveDeltas = {}, sentSinceTerminal = false, sentAtSeq = null } = {}) {
  if (!sentSinceTerminal) return false               // nothing sent → not working (zero-latency arm)
  const baseline = sentAtSeq != null ? sentAtSeq : latestRunStartedSeq(events) - 1
  if (runTerminated(events, baseline)) return false  // RUN_FINISHED/RUN_ERROR seq > sentAtSeq → done
  return true                                         // sent, no terminal yet → still working
}

// ── Item A — the PAUSED-for-human state, distinct from "thinking" ─────────────────────────────
// A money-propose / ask_user pause deliberately emits NO terminal event (a RUN_FINISHED/RUN_ERROR
// would clobber the blocked state on the TS side), so isWorking stays true for the whole human-wait
// window. The reliable pause signal is the durable CUSTOM `interaction` event: unresolved, at
// seq > sentAtSeq, with no terminal → the run is waiting on the human, not thinking.
//
// Resolution uses the SHARED rule (interactionResolution.js — X1): only a TOOL_CALL_RESULT carrying
// the interaction_id at payload TOP LEVEL (Sessions::Resume#tool_result_payload) resolves; the
// propose tool's own result does not — so this stays true through the whole wait and flips false
// exactly on approval/decline. The resumed run's RUN_STARTED then re-enters plain "thinking" via
// isWorking (unchanged).

// Internal: the first unresolved CUSTOM interaction after `baseline`, or null.
function firstUnresolvedInteraction(events, baseline) {
  const resolved = resolvedInteractionIdSet(events)
  for (const e of events || []) {
    if (
      e && e.type === "CUSTOM" && e.payload && e.payload.name === "interaction" &&
      e.seq > baseline && !resolved.has(e.payload.interaction_id)
    ) {
      return e
    }
  }
  return null
}

// True iff there is an unresolved durable CUSTOM interaction directive at seq > sentAtSeq AND no
// terminal at seq > sentAtSeq. Run-scoped via sentAtSeq (mirrors isWorking) so a PRIOR turn's
// already-resolved card can't trigger it. Fires for BOTH money-approval and ask_user pauses.
export function awaitingApproval({ events = [], sentAtSeq = null } = {}) {
  const baseline = sentAtSeq != null ? sentAtSeq : latestRunStartedSeq(events) - 1
  if (runTerminated(events, baseline)) return false   // run already settled → not a pause
  return firstUnresolvedInteraction(events, baseline) != null
}

// Which pause is it? Money approval requires a capability (→ "review above"); ask_user does not
// (→ "reply above"). The caller picks the directive copy off this.
export function awaitingApprovalRequiresCapability({ events = [], sentAtSeq = null } = {}) {
  const baseline = sentAtSeq != null ? sentAtSeq : latestRunStartedSeq(events) - 1
  const e = firstUnresolvedInteraction(events, baseline)
  return e != null && e.payload.requires_capability === true
}
